/**
 * Abstract Base Class for Property Type Handlers
 * 
 * This defines the contract that all property type handlers must follow.
 * Each property type (Apartment, House, Office, Warehouse, etc.) will have
 * its own handler that extends this base class.
 * 
 * Architecture Benefits:
 * - Single Responsibility: Each handler manages one property type
 * - Open/Closed: Easy to add new property types without modifying existing code
 * - Liskov Substitution: All handlers can be used interchangeably
 * - Dependency Inversion: Components depend on abstractions, not concrete implementations
 */

import { PropertyType } from '../../../shared/definitions/property';
import { RoomTypeConfig, FeatureConfig, FeatureCategory as FeatureScopeCategory } from '@core/services/property-analysis/property-room-config.service';

/**
 * Property Category - used for grouping similar property types
 */
export enum PropertyCategory {
  Residential = 'residential',
  Commercial = 'commercial',
  Industrial = 'industrial',
  Land = 'land',
  Mixed = 'mixed',
  Other = 'other'
}

/**
 * Room Type Category - used for organizing rooms into logical groups
 */
export enum RoomCategory {
  Core = 'core',               // Essential rooms (bedrooms, bathrooms, offices)
  Living = 'living',           // Living spaces (living room, dining room, family room)
  Functional = 'functional',   // Functional spaces (kitchen, laundry, storage)
  Recreational = 'recreational', // Recreation (gym, cinema, game room)
  Outdoor = 'outdoor',         // Outdoor spaces (balcony, terrace, patio)
  Commercial = 'commercial',   // Commercial spaces (meeting rooms, conference rooms)
  Industrial = 'industrial',   // Industrial spaces (warehouse sections, loading zones)
  Service = 'service'          // Service areas (mechanical, electrical, server rooms)
}

/**
 * Feature Category - used for organizing features into logical groups
 */
export enum FeatureCategory {
  Security = 'security',
  Amenities = 'amenities',
  Technology = 'technology',
  Energy = 'energy',
  Outdoor = 'outdoor',
  Storage = 'storage',
  Parking = 'parking',
  Industrial = 'industrial',
  Accessibility = 'accessibility'
}

/**
 * Validation rule for property-specific fields
 */
export interface PropertyValidationRule {
  fieldName: string;
  rules: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    custom?: (value: unknown) => boolean;
    errorMessage?: string;
  };
}

/**
 * Abstract base class that all property type handlers must extend
 */
export abstract class PropertyTypeHandler {
  /**
   * The property type this handler manages
   */
  abstract readonly propertyType: PropertyType;

  /**
   * The category this property type belongs to
   */
  abstract readonly category: PropertyCategory;

  /**
   * Human-readable name for this property type
   */
  abstract readonly displayName: string;

  /**
   * Plural form of the display name
   */
  abstract readonly pluralName: string;

  /**
   * Icon to represent this property type
   */
  abstract readonly icon: string;

  /**
   * Description of this property type
   */
  abstract readonly description: string;

  // ==================== ABSTRACT METHODS (Must be implemented by subclasses) ====================

  /**
   * Get all available room types for this property type
   * Each property type defines which rooms are relevant
   */
  abstract getRoomTypes(): RoomTypeConfig[];

  /**
   * Get all available features for this property type
   * Each property type defines which features are relevant
   */
  abstract getFeatures(): FeatureConfig[];

  /**
   * Get the main characteristics that should be displayed prominently
   * For example: Apartment -> bedrooms, bathrooms; Office -> workstations, meeting rooms
   */
  abstract getMainCharacteristics(): string[];

  /**
   * Get validation rules specific to this property type
   */
  abstract getValidationRules(): PropertyValidationRule[];

  // ==================== CONCRETE METHODS (Can be overridden if needed) ====================

  /**
   * Get room types filtered by category
   */
  getRoomTypesByCategory(category: RoomCategory): RoomTypeConfig[] {
    return this.getRoomTypes().filter(rt => {
      const rtCategory = (rt as RoomTypeConfig & { category?: RoomCategory }).category;
      return rtCategory === category || !rtCategory;
    });
  }

  /**
   * Get features filtered by category
   */
  getFeaturesByCategory(category: FeatureCategory): FeatureConfig[] {
    return this.getFeatures().filter(f => {
      // NOTE: FeatureConfig.category is typed as FeatureScopeCategory but at
      // runtime subclasses may populate it with FeatureCategory strings. The
      // comparison relies on string-value equality across enums.
      const fCategory = (f as unknown as { category?: string }).category;
      return fCategory === (category as unknown as string) || !fCategory;
    });
  }

  /**
   * Get a specific room type configuration by its key
   */
  getRoomTypeConfig(roomTypeKey: string): RoomTypeConfig | undefined {
    return this.getRoomTypes().find(rt => rt.typeKey === roomTypeKey);
  }

  /**
   * Get a specific feature configuration by its key
   */
  getFeatureConfig(featureKey: string): FeatureConfig | undefined {
    return this.getFeatures().find(f => f.key === featureKey);
  }

  /**
   * Check if a room type is available for this property type
   */
  hasRoomType(roomTypeKey: string): boolean {
    return this.getRoomTypes().some(rt => rt.typeKey === roomTypeKey);
  }

  /**
   * Check if a feature is available for this property type
   */
  hasFeature(featureKey: string): boolean {
    return this.getFeatures().some(f => f.key === featureKey);
  }

  /**
   * Get property-level features (PROPERTY_ONLY or FLEXIBLE with property scope)
   * These are managed at the individual property/unit level
   */
  getPropertyFeatures(): FeatureConfig[] {
    return this.getFeatures().filter(f => 
      f.category === FeatureScopeCategory.PROPERTY_ONLY || 
      (f.category === FeatureScopeCategory.FLEXIBLE && f.scopeType === 'property')
    );
  }

  /**
   * Get building-level features (BUILDING_ONLY or FLEXIBLE with building scope)
   * These are managed at the building level and inherited by units
   */
  getBuildingFeatures(): FeatureConfig[] {
    return this.getFeatures().filter(f => 
      f.category === FeatureScopeCategory.BUILDING_ONLY || 
      (f.category === FeatureScopeCategory.FLEXIBLE && f.scopeType === 'building')
    );
  }

  /**
   * Get flexible features (can be either property or building level)
   */
  getFlexibleFeatures(): FeatureConfig[] {
    return this.getFeatures().filter(f => f.category === FeatureScopeCategory.FLEXIBLE);
  }

  /**
   * Get the default form values for a new property of this type
   */
  getDefaultFormValues(): Record<string, unknown> {
    return {
      type: this.propertyType,
      characteristics: this.getDefaultCharacteristics()
    };
  }

  /**
   * Get default characteristics for this property type
   * Can be overridden by subclasses
   */
  protected getDefaultCharacteristics(): Record<string, unknown> {
    return {};
  }

  /**
   * Validate property data specific to this type
   * Returns array of error messages, empty if valid
   */
  validate(propertyData: unknown): string[] {
    const errors: string[] = [];
    const rules = this.getValidationRules();

    rules.forEach(rule => {
      const value = this.getNestedValue(propertyData, rule.fieldName);
      const { required, min, max, pattern, custom, errorMessage } = rule.rules;

      if (required && (value === null || value === undefined || value === '')) {
        errors.push(errorMessage || `${rule.fieldName} is required`);
      }

      if (min !== undefined && typeof value === 'number' && value < min) {
        errors.push(errorMessage || `${rule.fieldName} must be at least ${min}`);
      }

      if (max !== undefined && typeof value === 'number' && value > max) {
        errors.push(errorMessage || `${rule.fieldName} must be at most ${max}`);
      }

      if (pattern && typeof value === 'string' && !new RegExp(pattern).test(value)) {
        errors.push(errorMessage || `${rule.fieldName} format is invalid`);
      }

      if (custom && !custom(value)) {
        errors.push(errorMessage || `${rule.fieldName} validation failed`);
      }
    });

    return errors;
  }

  /**
   * Helper to get nested object values
   */
  private getNestedValue(obj: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>(
      (current, prop) => (current != null && typeof current === 'object')
        ? (current as Record<string, unknown>)[prop]
        : undefined,
      obj
    );
  }

  /**
   * Get recommended minimum surface area for this property type (in m²)
   * Can be overridden by subclasses
   */
  getRecommendedMinSurface(): number {
    return 0; // Default: no minimum
  }

  /**
   * Get typical price range multiplier compared to base property
   * Can be overridden by subclasses
   * 1.0 = base, 1.5 = 50% more expensive, etc.
   */
  getPriceRangeMultiplier(): number {
    return 1.0; // Default: no multiplier
  }
}

